// @ts-check
// =============================================================================
// js/net/socket.js — the one socket.io connection.
//
// WHAT THIS IS
//   Owns the realtime channel (old client: SetupSocket :6706). It connects
//   same-origin (decision from REBUILD_PLAN §6: io() with no URL, so the
//   client talks to whatever host served it), authenticates with
//   "name + ' ' + token", and translates server events into store updates:
//
//     'Lobby Update'    {duels, deets}  -> store.lobby
//     'Duel Update'     duel            -> queued via the duel-update callback
//                                          (animations need old-vs-new diffing,
//                                          so the duel screen owns that logic)
//     'User Update'     {update, packs} -> store.userData.collection.packs
//     'Initiate'        {serverStartTime, clientBuild} -> build-change check
//     'Server Pausing'  {reason}        -> heads-up: restart imminent, go 🟡
//     'Session Expired' (no payload)    -> GATED silent re-login (this module
//                                          owns the permission gate below;
//                                          session.js registers the actual
//                                          re-login via
//                                          setSessionExpiredHandler)
//     connect/disconnect/errors         -> store.connection
//
//   Screens SEND exactly one thing: sendAction('Play__…') (old SocketAction).
//
// RESTART GRACE (docs/RESTART_GRACE.md, phase 2 + appendix)
//   A restart is not a reason to reload — a CHANGED CLIENT BUILD is. BOTH
//   build outputs are content-hashed (/client.<hash>.js + /client.<hash>.css),
//   so the build identity is the PAIR "<jsHash>:<cssHash>": read at boot from
//   this page's own script + stylesheet tags, compared against the identical
//   pair the server parses out of its index.html ('Initiate' and
//   GET /serverStartTime both carry clientBuild). A CSS-only deploy is still
//   a client change. The takeover + reload fires ONLY when both sides are
//   non-empty and differ; '' on either side (an unhashed dev shell, or a
//   server that predates the field) means never force-reload.
//
//   The connection lifecycle around a deploy:
//     'Server Pausing' (the server's SIGINT/SIGTERM hook, a courtesy that may
//       never arrive on a crash) -> orb 🟡 + restartExpected: the drop that
//       follows is scheduled quiet, AND an announced pause is one of the
//       silent re-login permissions below;
//     disconnect / connect_error -> stay 🟡 while socket.io auto-reconnects
//       and the fast poll (12 × 5 s against /serverStartTime) hunts;
//     poll answers -> changed build: takeover + reload; same/unknown build:
//       the server is back — hurry the reconnect (the server re-pushes lobby
//       + any restored duel on connect, so a same-build episode is just
//       🟢 → 🟡 → 🟢, no reload, nothing lost);
//     fast-poll budget exhausted -> orb 🔴 AND the slow poll (30 s, forever)
//       arms: a server-initiated disconnect means socket.io will NOT retry on
//       its own, so without the slow poll a tab would sit red until a manual
//       reload even after the server recovered. 'down' is always recoverable;
//       a successful connect tears the slow poll down.
//
//   SILENT RE-LOGIN PERMISSION (the 'Session Expired' gate — appendix row):
//     allowed iff not superseded AND (restartExpected — a pause was announced
//     — OR the server spoke to this tab within the last ~3 minutes OR the tab
//     is visible right now). A hidden, long-dormant tab must NOT re-login:
//     re-minting the token would supersede whatever device the account is
//     actively playing on. Declined -> 'down', every poll stops, and ONE
//     visibilitychange listener re-runs the recovery when the tab is next
//     seen (visibility then satisfies the gate). A cap (~3 attempts per
//     2 minutes) backstops the gate so a server that keeps expiring us — or
//     an identity that resolves nowhere — can't drive a kick loop.
//     restartExpected clears on an AUTHENTICATED connect ('Initiate') and
//     when a takeover fires — never on the raw transport 'connect', which
//     precedes the very 'Session Expired' the flag must gate.
//
//   While not-connected, sendAction/sendChat DROP (never queue): a fresh
//   'Duel Update' arrives on reconnect so the board self-corrects, and
//   replaying a stale action against a restored duel is how desyncs are born.
// =============================================================================

import { io } from 'socket.io-client';
import { store, setSlice, notify } from '../core/store.js';

/** @type {import('socket.io-client').Socket|null} */
let socket = null;

/**
 * The build identity of the page THIS tab is running: BOTH content hashes,
 * read from the tags the template injects (the /client.<hash>.js script and
 * the /client.<hash>.css stylesheet), joined "<jsHash>:<cssHash>" — the same
 * shape the server parses out of its index.html, so a CSS-only deploy still
 * reads as a build change. '' when NEITHER tag has a hash (an unhashed dev
 * shell) — and '' means "never force-reload off a build comparison".
 * @returns {string}
 */
function readOwnBuild() {
	const js = (document.querySelector('script[src^="/client."]')?.getAttribute('src') || '')
		.match(/client\.([A-Z0-9]+)\.js/i)?.[1] || '';
	const css = (document.querySelector('link[href^="/client."]')?.getAttribute('href') || '')
		.match(/client\.([A-Z0-9]+)\.css/i)?.[1] || '';
	return js || css ? js + ':' + css : '';
}

/** Read once at boot: the page's bundle can't change without a reload. */
const ownBuild = readOwnBuild();

/**
 * The reload rule (docs/RESTART_GRACE.md appendix): reload ONLY when both our
 * pair and the server-reported one are non-empty and differ. Either side
 * blank = "can't tell" = leave the page alone.
 * @param {unknown} clientBuild  The server-reported pair (Initiate / poll).
 * @returns {boolean}
 */
function buildChanged(clientBuild) {
	const serverBuild = String(clientBuild || '');
	return Boolean(ownBuild && serverBuild && serverBuild !== ownBuild);
}

/** True once THIS tab has been superseded by a newer login of the same account. */
let superseded = false;

/**
 * True between a 'Server Pausing' heads-up and the next AUTHENTICATED connect
 * ('Initiate') or takeover: the drop that follows is a scheduled deploy, not
 * an anomaly — and an announced pause is one of the silent re-login
 * permissions (a tab the server said goodbye to may come back quietly, even
 * hidden). Deliberately NOT cleared on the raw transport 'connect': that
 * event precedes the very 'Session Expired' this flag must gate on a kicked
 * reconnect.
 */
let restartExpected = false;

/**
 * When the server last SPOKE to this tab over an authenticated socket (any
 * event except the kick/supersede ones — see the onAny hook in connectSocket).
 * This is the "connected within the last ~3 minutes" half of the re-login
 * permission gate. Deliberately NOT refreshed on the raw 'connect' (which
 * precedes the 'Session Expired' of a kicked reconnect and would make the
 * recency check trivially true) and NOT timer-driven (a sleeping laptop
 * freezes timers, then fires them on wake — reading as "fresh" hours later):
 * only genuine server traffic counts, so a long-dormant tab reads dormant.
 */
let lastConnectedAt = 0;

/** The permission gate's "connected recently" window (appendix: ~3 minutes). */
const RECENTLY_CONNECTED_MS = 3 * 60 * 1000;

/**
 * Loop guard: at most RELOGIN_CAP silent re-login attempts per rolling
 * RELOGIN_CAP_WINDOW_MS. A server that keeps expiring us — or an identity
 * that resolves nowhere, whose "re-login" is a no-op — would otherwise cycle
 * kick -> poll -> connect -> kick every few seconds forever. Tripping the cap
 * counts as a declined re-login (down + wait to be seen). Cleared on
 * 'Initiate': an authenticated connect means the attempts weren't a loop.
 */
const RELOGIN_CAP = 3;
const RELOGIN_CAP_WINDOW_MS = 2 * 60 * 1000;
/** @type {number[]} Timestamps of recent silent re-login attempts. */
let reloginAttempts = [];

/**
 * True after a DECLINED silent re-login (the gate said no, or the cap
 * tripped): the tab settles at 🔴 with every poll stopped — no reconnect
 * hurrying, no background kicking — until the one-shot visibilitychange
 * listener sees the tab again and re-runs the recovery.
 */
let dormant = false;

/**
 * session.js registers its silent re-login here (importing it directly would
 * be circular: session.js already imports connectSocket from this module).
 * @type {(() => void)|null}
 */
let onSessionExpired = null;

/**
 * Register the 'Session Expired' response — session.js wires its silent
 * re-login through the stored reference (accounts) or guestReference (guests).
 * Pass null to reset.
 * @param {(() => void)|null} handler
 * @returns {void}
 */
export function setSessionExpiredHandler(handler) {
	onSessionExpired = handler || null;
}

/**
 * The store-only fallback used when no duel screen is mounted (covers
 * reconnect-before-screen-mounts). One carve-out: the server's FINAL push
 * after a leave (GameOver/GameClose, sent right before it deletes the duel)
 * can land AFTER we already cleared store.duel and went home — accepting it
 * would resurrect the dead game (the nav's Game button reappears and leads
 * into a game with no legal actions left). Ended duels only count if we're
 * still holding that same duel.
 * @param {any} duel
 * @returns {void}
 */
function storeDuelUpdate(duel) {
	if ((duel?.GameOver || duel?.GameClose) && store.duel?.id !== duel?.id) return;
	setSlice('duel', duel);
}

/**
 * The duel screen registers the real handler; until then updates just land in
 * the store.
 * @type {(duel: any) => void}
 */
let onDuelUpdate = storeDuelUpdate;

/**
 * In-duel chat sink (the log registers it on mount). Default: drop the message.
 * @type {(payload: {id:string, nickname:string, message:string}) => void}
 */
let onChat = () => {};

/**
 * Register the chat-message handler (the battle log). Pass null to reset.
 * @param {((payload: {id:string, nickname:string, message:string}) => void)|null} handler
 * @returns {void}
 */
export function setChatHandler(handler) {
	onChat = handler || (() => {});
}

/**
 * Send an in-duel chat message (paid users only — server re-checks).
 * @param {string} message
 * @returns {void}
 */
export function sendChat(message) {
	if (!socket || !store.userData || !store.duel) {
		return;
	}
	// While reconnecting, drop — never queue (see header: stale replays breed
	// desyncs, and chat repeated out of context reads as a glitch anyway).
	if (store.connection !== 'connected') {
		console.warn(`sendChat dropped while ${store.connection}`);
		return;
	}
	socket.emit('Chat', {
		token: store.userData.name + ' ' + store.session.token,
		duel: store.duel.id,
		message,
	});
}

/**
 * Let the duel module take over 'Duel Update' handling (it queues updates for
 * animation). Pass null to restore the default store-only behavior.
 * @param {((duel: any) => void)|null} handler
 * @returns {void}
 */
export function setDuelUpdateHandler(handler) {
	onDuelUpdate = handler || storeDuelUpdate;
}

/**
 * Feed a duel state through the SAME path a socket 'Duel Update' takes.
 * Used by the ?debug tooling (js/app/debug.js) to exercise the duel screen
 * without a live opponent. No-op in normal play.
 * @param {any} duel
 * @returns {void}
 */
export function simulateDuelUpdate(duel) {
	onDuelUpdate(duel);
}

/**
 * Feed a chat message through the same path a socket 'Chat Message' takes
 * (?debug tooling only — exercises the log without a paid opponent).
 * @param {{id:string, nickname:string, message:string}} payload
 * @returns {void}
 */
export function simulateChat(payload) {
	onChat(payload);
}

/**
 * Open (or re-open) the authenticated socket. Called by session.js right
 * after a successful login.
 * @param {string} name   userData.name (the secret account id).
 * @param {string} token  The fresh session token from /login.
 * @returns {void}
 */
export function connectSocket(name, token) {
	// A relogin replaces the old connection outright.
	if (socket) {
		socket.disconnect();
	}

	// Same-origin connection: no hardcoded host (migration guide §8).
	socket = io({ auth: { token: name + ' ' + token } });

	// Every server event on this socket refreshes "the server spoke to us" —
	// the recency half of the re-login permission gate. The two kick events
	// are excluded: 'Session Expired' IS the kick the gate judges (counting it
	// would make recency self-satisfying), and a superseded tab must read as
	// dormant, not fresh.
	socket.onAny((event) => {
		if (event === 'Session Expired' || event === 'Session Superseded') return;
		lastConnectedAt = Date.now();
	});

	socket.on('connect', () => {
		// NOTE: restartExpected is NOT cleared here — the raw transport connect
		// precedes the 'Session Expired' of a kicked reconnect, and the flag
		// must still be standing when that gate check runs. It clears on
		// 'Initiate' (an authenticated connect) or when a takeover fires.
		dormant = false;
		stopSlowPoll();
		setSlice('connection', 'connected');
	});

	socket.on('disconnect', () => {
		// A declined re-login already settled this tab at 🔴 deliberately — the
		// server's follow-up hangup must not repaint it yellow or restart the
		// reconnect machinery (dormancy ends via the visibility listener, not
		// via more connection churn).
		if (dormant) {
			setSlice('connection', 'down');
			return;
		}
		// Yellow, not red: socket.io auto-reconnects underneath, and most drops
		// are a deploy blip or a flaky network. Red ('down') is reserved for the
		// poll below giving up.
		setSlice('connection', 'reconnecting');
		// A duplicate-login kick is NOT a server restart — don't poll/reload.
		if (superseded) return;
		watchForServerRestart();
	});

	socket.on('Session Superseded', () => {
		// This account opened elsewhere (another tab/device) and took control.
		superseded = true;
		restartExpected = false; // takeover: the pause story is over for this tab
		showSupersededTakeover();
	});

	socket.on('Session Expired', () => {
		// The server names the auth failure right before hanging up — the normal
		// state after a restart (the token table lives in server RAM). Response:
		// silent re-login via the handler session.js registered — but ONLY when
		// the permission gate allows it and the attempt cap has headroom;
		// otherwise settle at 🔴 and wait to be seen (reloginPermitted /
		// declineRelogin below). A superseded tab staying kicked is correct, so
		// it never re-logins here.
		if (superseded) return;
		if (!reloginPermitted() || overReloginCap()) {
			declineRelogin();
			return;
		}
		reloginAttempts.push(Date.now());
		onSessionExpired?.();
	});

	socket.on('Server Pausing', () => {
		// The server's shutdown hook says a restart is seconds away. Go yellow
		// NOW so the drop that follows reads as scheduled quiet — no error UI,
		// no reload; the post-reconnect build check decides if anything drastic
		// happens. restartExpected also grants the silent re-login permission:
		// a tab the server said goodbye to may come back quietly, even hidden.
		restartExpected = true;
		setSlice('connection', 'reconnecting');
	});

	socket.on('connect_error', () => {
		// Still trying (socket.io keeps retrying underneath). The old heuristic
		// that force-reloaded on a "closed before established" message is gone:
		// during a pm2 swap that error is routine, and the build-hash comparison
		// after reconnect now makes the reload decision correctly.
		if (dormant) return; // a declined tab stays red + quiet until seen
		setSlice('connection', 'reconnecting');
	});

	socket.on('Lobby Update', (message) => {
		setSlice('lobby', { duels: message.duels || [], deets: message.deets || {} });
	});

	socket.on('Duel Update', (newDuel) => {
		onDuelUpdate(newDuel);
	});

	socket.on('Chat Message', (payload) => {
		onChat(payload);
	});

	socket.on('User Update', (data) => {
		// Mid-session pack drops (server: NotifyUser). Mutate + notify so the
		// rewards badge and shelf update live.
		if (data.update === 'packs' && store.userData?.collection) {
			store.userData.collection.packs = data.packs;
			notify('userData');
		}
	});

	socket.on('Initiate', (data) => {
		// The server's hello — which only ever follows a SUCCESSFUL auth, so
		// this is the authenticated connect: the announced pause (if any) has
		// fully resolved, and the recent re-login attempts evidently weren't a
		// kick loop.
		restartExpected = false;
		reloginAttempts = [];
		// It also names the client build the server now serves. Reload ONLY on
		// a real build change — a restart that serves the same bytes is
		// nobody's business (the server re-pushes lobby + any restored duel on
		// connect, so a same-build reconnect resyncs without ceremony).
		if (buildChanged(data?.clientBuild)) {
			showRebootTakeover();
		}
	});
}

/**
 * Send a gameplay action over the socket (old client: SocketAction :6907).
 * @param {string} action  A protocol string from js/net/protocol.js.
 * @returns {void}
 */
export function sendAction(action) {
	if (!socket || !store.userData || !store.duel) {
		console.error('sendAction dropped (no socket/user/duel):', action);
		return;
	}
	// While reconnecting, drop — never queue. A fresh 'Duel Update' arrives on
	// reconnect so the board self-corrects; replaying a stale queued action
	// against a restored duel is how desyncs are born. For a turn-based game a
	// short input freeze is the honest behavior.
	if (store.connection !== 'connected') {
		console.warn(`sendAction dropped while ${store.connection}:`, action);
		return;
	}
	socket.emit('Perform Action', {
		token: store.userData.name + ' ' + store.session.token,
		duel: store.duel.id,
		action,
	});
}

// -----------------------------------------------------------------------------
// Server-restart detection (old client: CheckForNewServer :6873).
// After a disconnect, poll /serverStartTime — the contract answer is
// { serverStartTime, clientBuild }. An answer with a DIFFERENT build means a
// new client deployed -> takeover + reload. An answer with the same (or
// unknowable) build means the server is back -> reconnect. No answer inside
// the fast budget -> 'down' (🔴) — but never FINALLY down: the slow poll
// (30 s, forever) keeps 'down' recoverable, because a server-initiated
// disconnect means socket.io will NOT retry on its own, and without anything
// armed the tab would sit red until a manual reload even after the server
// recovered. Both polls share one decision (handlePollAnswer).
// -----------------------------------------------------------------------------

/** @type {ReturnType<typeof setInterval>|null} The fast (12 × 5 s) poll. */
let fastPollTimer = null;
/** @type {ReturnType<typeof setInterval>|null} The slow (30 s, forever) poll. */
let slowPollTimer = null;

/** Stop the fast poll (idempotent). */
function stopFastPoll() {
	if (fastPollTimer) {
		clearInterval(fastPollTimer);
		fastPollTimer = null;
	}
}

/** Stop the slow poll (idempotent). Torn down by 'connect', a takeover, or
 *  dormancy (declineRelogin). */
function stopSlowPoll() {
	if (slowPollTimer) {
		clearInterval(slowPollTimer);
		slowPollTimer = null;
	}
}

/**
 * The shared decision for BOTH polls, exactly the appendix's: a contract-
 * shaped answer with a CHANGED build -> takeover + reload; same/unknown
 * build -> the server is back, hurry socket.io along instead of waiting out
 * its backoff ('connect' turns the orb 🟢, and any re-login rides the
 * 'Session Expired' permission gate). A non-contract answer (a proxy error
 * page that happens to parse as JSON) decides nothing.
 * @param {any} data  Parsed /serverStartTime body.
 * @returns {boolean}  true when the answer settled things (the fast poll can
 *                     stop; the slow poll stays armed until 'connect' lands).
 */
function handlePollAnswer(data) {
	if (!data?.serverStartTime) return false;
	if (buildChanged(data.clientBuild)) {
		showRebootTakeover(); // stops both polls itself
		return true;
	}
	socket?.connect();
	return true;
}

/** Fast poll: up to a minute after a disconnect, to catch a mid-session deploy. */
function watchForServerRestart() {
	if (fastPollTimer) return;
	let attempts = 0;
	fastPollTimer = setInterval(() => {
		// socket.io may beat the poll to it (its own retry loop reconnected):
		// then 'Initiate' already ran the build check — nothing left to watch.
		if (store.connection === 'connected') {
			stopFastPoll();
			return;
		}
		fetch('/serverStartTime')
			.then((res) => res.json())
			.then((data) => {
				if (handlePollAnswer(data)) stopFastPoll();
			})
			.catch(() => {
				/* server still down; keep polling until attempts run out */
			});
		attempts++;
		if (attempts >= 12) {
			stopFastPoll();
			// A minute without an answer: give up LOUDLY (🔴) but never finally —
			// arm the slow poll so a late recovery still flips us back to 🟢.
			if (store.connection !== 'connected') {
				setSlice('connection', 'down');
				armSlowPoll();
			}
		}
	}, 5_000);
}

/**
 * Slow poll (~30 s, forever, duplicate-guarded): the appendix's "giving up /
 * recovering" row — 'down' must always be recoverable. Same decisions as the
 * fast poll's (handlePollAnswer); torn down by 'connect', by a takeover, or
 * by dormancy.
 * @returns {void}
 */
function armSlowPoll() {
	if (slowPollTimer) return;
	slowPollTimer = setInterval(() => {
		if (store.connection === 'connected') {
			stopSlowPoll();
			return;
		}
		fetch('/serverStartTime')
			.then((res) => res.json())
			.then(handlePollAnswer)
			.catch(() => {
				/* still down — the slow poll never gives up */
			});
	}, 30_000);
}

// -----------------------------------------------------------------------------
// The silent re-login permission gate + dormancy (docs/RESTART_GRACE.md
// appendix, "silent re-login permission" row). The 'Session Expired' handler
// above consults these before invoking session.js's re-login.
// -----------------------------------------------------------------------------

/**
 * Allowed iff not superseded AND (a pause was announced, OR the server spoke
 * to this tab within ~3 minutes, OR the tab is visible right now). A hidden,
 * long-dormant tab fails all three — re-minting its token would supersede
 * whatever device the account is actively playing on now.
 * @returns {boolean}
 */
function reloginPermitted() {
	if (superseded) return false;
	if (restartExpected) return true;
	if (lastConnectedAt && Date.now() - lastConnectedAt < RECENTLY_CONNECTED_MS) return true;
	return document.visibilityState === 'visible';
}

/**
 * Prune expired attempts, then test the loop-guard cap (see the constants).
 * @returns {boolean}  true when another attempt would exceed the cap.
 */
function overReloginCap() {
	const cutoff = Date.now() - RELOGIN_CAP_WINDOW_MS;
	reloginAttempts = reloginAttempts.filter((at) => at > cutoff);
	return reloginAttempts.length >= RELOGIN_CAP;
}

/**
 * A silent re-login was declined (gate or cap): settle at 🔴, stop every poll
 * (no reconnect hurrying, no background kicking), and arm the ONE recovery-
 * on-visibility shot.
 * @returns {void}
 */
function declineRelogin() {
	dormant = true;
	stopFastPoll();
	stopSlowPoll();
	setSlice('connection', 'down');
	armVisibilityRecovery();
}

/** Guards armVisibilityRecovery against stacking listeners. */
let visibilityRecoveryArmed = false;

/**
 * One-shot: when the tab next becomes visible, wake from dormancy and re-run
 * the whole recovery — poll, build check, reconnect; the re-login is now
 * permitted by visibility. The attempt cap resets too: this is a fresh,
 * human-witnessed recovery, not a continuation of the loop the cap stopped.
 * @returns {void}
 */
function armVisibilityRecovery() {
	if (visibilityRecoveryArmed) return;
	visibilityRecoveryArmed = true;
	const onVisible = () => {
		if (document.visibilityState !== 'visible') return;
		document.removeEventListener('visibilitychange', onVisible);
		visibilityRecoveryArmed = false;
		dormant = false;
		reloginAttempts = [];
		if (store.connection === 'connected') return;
		setSlice('connection', 'reconnecting');
		watchForServerRestart();
	};
	document.addEventListener('visibilitychange', onVisible);
}

/**
 * Full-page "opened elsewhere" takeover with a Play button that reclaims control
 * for THIS tab. Reloading re-logs in and reconnects, which kicks whichever
 * tab/device currently holds the session (the server supersedes the old socket).
 * @returns {void}
 */
function showSupersededTakeover() {
	if (document.querySelector('.superseded-takeover')) return;
	const el = document.createElement('div');
	el.className = 'reboot-takeover superseded-takeover';
	const box = document.createElement('div');
	box.style.cssText = 'max-width:32rem;padding:1.25rem;text-align:center;';
	const title = document.createElement('div');
	title.style.cssText = 'font-size:1.25rem;margin-bottom:0.75rem;font-weight:bold;';
	title.textContent = 'Opened Somewhere Else';
	const body = document.createElement('div');
	body.style.cssText = 'margin-bottom:1.25rem;line-height:1.5;';
	body.textContent = 'This game is now open in another tab or on another device, so this one went idle. Press Play to take control back here.';
	const play = document.createElement('button');
	play.className = 'defaultButton';
	play.textContent = 'Play';
	// Reload -> re-login -> reconnect, which supersedes the other session.
	play.addEventListener('click', () => window.location.reload());
	box.append(title, body, play);
	el.appendChild(box);
	document.body.appendChild(el);
}

/** Full-page "new version deploying" takeover, then reload (old :6820). */
function showRebootTakeover() {
	// The reload decision is made: the pause story is over for this tab, and
	// neither poll has anything left to decide.
	restartExpected = false;
	stopFastPoll();
	stopSlowPoll();
	if (window._reloadingSoon) return;
	window._reloadingSoon = true;
	const takeover = document.createElement('div');
	takeover.className = 'reboot-takeover';
	takeover.textContent = 'NEW VERSION DEPLOYING — REBOOTING IN FIVE SECONDS!';
	document.body.appendChild(takeover);
	setTimeout(() => window.location.reload(), 5_000);
}
